import type { GetTradesRow } from '@morfi/backend-sdk-v2/client';

import { resolveTradeSide } from '@/lib/chart/trade-side';
import type { LiveTrade } from './types';

/**
 * Map a `useGetTrades` list-hook result to the overlay's `LiveTrade[]` shape.
 *
 * `ids` is the list hook's set-stable id array — it is authoritative for
 * order and only changes identity when a trade is added/removed. `rows` is
 * the row-payload escape valve. A trade row is immutable once recorded, so
 * the parent should `useMemo` the result on `[rows, ids]`: a conviction-only
 * re-render leaves both references untouched, so the mapped array keeps its
 * identity and the per-outcome overlays bail out under the React Compiler.
 *
 * The result spans ALL outcomes; each `LiveTradesOverlay` filters to its own
 * `outcomeIndex`.
 */
export function tradesRowsToLiveTrades(
  rows: readonly GetTradesRow[],
  ids: readonly string[],
): LiveTrade[] {
  const byEventId = new Map<string, GetTradesRow>(rows.map((r) => [r.eventId, r]));
  const out: LiveTrade[] = [];
  for (const id of ids) {
    const r = byEventId.get(id);
    if (r !== undefined && r.usdcAmount !== undefined) {
      const side = resolveTradeSide(r);
      if (side === undefined) continue;
      // ALLOW-ARRAY-MUTATION: building a fresh local array inside a pure helper, not state/props.
      const liveTrade: LiveTrade = {
        id: r.eventId,
        outcomeIndex: r.outcomeIndex,
        side,
        amountD6: r.usdcAmount,
      };
      out.push(liveTrade); // ALLOW-ARRAY-MUTATION: local accumulator array, not state/props.
    }
  }
  return out;
}
